/**
 * Signup Wizard - Main Entry Point
 * Composes all signup modules into an Alpine.js component
 */

import { getDayOptions, getYearOptions, getLocaleDateFormat } from './utils/dateHelpers.js';
import { loadState, saveState, clearState } from './state/localStorage.js';
import { initializeFormData } from './state/formData.js';
import { isStepValid, getStepError, getFirstInvalidStep, markStepTouched } from './validation/stepValidation.js';
import { focusCurrentInput, isCityStep, getCityInputId } from './navigation/stepNavigation.js';
import { requestLocation } from './geolocation/index.js';
import { initializeCityAutocomplete, cleanupCityAutocomplete } from './autocomplete/cityAutocomplete.js';
import { submitSignup, getFieldStepMap } from './submission/apiSubmission.js';

export function signupWizard() {
    // Load saved state before Alpine initializes
    const savedState = loadState();
    const initialFormData = initializeFormData(savedState || {});

    return {
        // Spread initial form data
        ...initialFormData,

        // Autocomplete instances (for cleanup)
        _autocompleteInstances: {},

        // Computed properties
        get progress() {
            return (this.currentStep / this.totalSteps) * 100;
        },

        get canProceed() {
            return isStepValid(this.currentStep, this);
        },

        get isLastStep() {
            return this.currentStep === this.totalSteps;
        },

        get isFirstStep() {
            return this.currentStep === 1;
        },

        get currentStepError() {
            // Make error reactive by accessing errors and touched directly
            return getStepError(this.currentStep, this);
        },

        get dayOptions() {
            return getDayOptions();
        },

        get yearOptions() {
            return getYearOptions();
        },

        get dateFormat() {
            return getLocaleDateFormat();
        },

        // Initialization
        init(config = {}) {
            // Apply server config if provided
            if (Object.keys(config).length > 0) {
                const formData = initializeFormData(config);
                Object.assign(this, formData);
            }

            // Set up auto-save watchers
            this.setupAutoSave();

            // Initialize UI after Alpine is ready
            this.$nextTick(() => {
                this.markAlpineReady();

                // Show content after initialization is complete
                this.showContent = true;

                // Wait for transition to complete before enabling validation
                setTimeout(() => {
                    this.isInitializing = false;
                    this.focusCurrentInput();
                }, 350);

                // Initialize city autocomplete if on city step at page load
                if (isCityStep(this.currentStep)) {
                    this.initLocationAutocomplete();
                    // Don't request location here - wait for user navigation to maintain gesture chain
                }
            });
        },

        // Auto-save setup
        setupAutoSave() {
            const fieldsToWatch = [
                'name', 'email', 'day', 'month', 'year', 'sex',
                'username', 'password', 'cityId', 'cityName', 'cityCountryIso',
                'homeCityId', 'homeCityName', 'homeCityCountryIso', 'currentStep'
            ];

            fieldsToWatch.forEach(field => {
                this.$watch(field, () => saveState(this));
            });
        },

        markAlpineReady() {
            const form = document.getElementById('signupForm');
            if (form && form.closest('[x-data]')) {
                form.closest('[x-data]').classList.add('alpine-ready');
            }
        },

        // State management
        loadState,
        clearState,

        // Validation
        isStepValid(step) {
            return isStepValid(step, this);
        },

        getStepError() {
            return getStepError(this.currentStep, this);
        },

        getFirstInvalidStep() {
            return getFirstInvalidStep(this, this.totalSteps);
        },

        markTouched() {
            markStepTouched(this.currentStep, this);
        },

        // Navigation
        async next() {
            if (this.isSubmitting) return;

            this.markTouched();

            if (!this.canProceed) {
                return;
            }

            // Submit if on last step
            if (this.isLastStep) {
                await this.submitSignup();
                return;
            }

            const nextStep = this.currentStep + 1;

            // Request location if going to city step
            if (isCityStep(nextStep) && !this.locationRequested) {
                this.requestLocationAndShowDropdown();
            }

            // Change step immediately to maintain user gesture chain for mobile keyboard
            this.isTransitioning = true;
            this.currentStep = nextStep;

            await this.$nextTick();

            this.isTransitioning = false;
            this.afterStepChange();
        },

        async back() {
            if (this.isFirstStep) return;

            const prevStep = this.currentStep - 1;

            // Request location if going to city step
            if (isCityStep(prevStep) && !this.locationRequested) {
                this.requestLocationAndShowDropdown();
            }

            this.isTransitioning = true;
            this.currentStep = prevStep;

            await this.$nextTick();

            this.isTransitioning = false;
            this.afterStepChange();
        },

        async goToStep(step) {
            if (step < 1 || step > this.totalSteps) return;
            if (step > this.currentStep && !this.canProceed) return;

            // Request location if going to city step
            if (isCityStep(step) && !this.locationRequested) {
                this.requestLocationAndShowDropdown();
            }

            this.isTransitioning = true;
            this.currentStep = step;

            await this.$nextTick();

            this.isTransitioning = false;
            this.afterStepChange();
        },

        afterStepChange() {
            // Focus input immediately for mobile keyboard
            this.focusCurrentInput();

            // Initialize city autocomplete if on city step
            if (isCityStep(this.currentStep)) {
                this.initLocationAutocomplete();
            }
        },

        focusCurrentInput() {
            focusCurrentInput(this.currentStep);
        },

        // Sex selection
        selectSex(value) {
            this.sex = value;
            this.touched.sex = true;
        },

        // Geolocation
        async requestLocationAndShowDropdown() {
            // Skip if already completed
            if (this.locationRequested && this.locationReady) {
                if (isCityStep(this.currentStep) && this.shouldShowDropdown()) {
                    this.showCityDropdown();
                }
                return;
            }

            // Skip if already requesting
            if (this.locationRequested) {
                return;
            }

            this.locationRequested = true;

            const coords = await requestLocation();
            if (coords) {
                this.userLat = coords.lat;
                this.userLon = coords.lon;
            }
            this.locationReady = true;
            saveState(this);

            // Show dropdown if on city step
            if (isCityStep(this.currentStep) && this.shouldShowDropdown()) {
                this.showCityDropdown();
            }
        },

        shouldShowDropdown() {
            return (this.currentStep === 4 && !this.cityId) ||
                   (this.currentStep === 5 && !this.homeCityId);
        },

        showCityDropdown() {
            const inputId = getCityInputId(this.currentStep);
            const input = document.getElementById(inputId);
            if (input) {
                setTimeout(() => input.focus(), 100);
            }
        },

        // City autocomplete
        initLocationAutocomplete() {
            this.cleanupLocationAutocomplete();

            let $field;
            let initialCountryIso = '';
            if (this.currentStep === 4) {
                $field = $('input[name="_userLocation"]');
                initialCountryIso = this.cityCountryIso;
            } else if (this.currentStep === 5) {
                $field = $('input[name="_userOrigin"]');
                initialCountryIso = this.homeCityCountryIso;
            } else {
                return;
            }

            if (!$field.length) return;

            const isHomeCity = this.currentStep === 5;
            const instance = initializeCityAutocomplete($field, {
                getCoordinates: () => ({ lat: this.userLat, lon: this.userLon }),
                onCitySelect: (cityId, cityName, countryIso) => {
                    if (isHomeCity) {
                        this.homeCityId = cityId;
                        this.homeCityName = cityName;
                        this.homeCityCountryIso = countryIso || '';
                    } else {
                        this.cityId = cityId;
                        this.cityName = cityName;
                        this.cityCountryIso = countryIso || '';
                    }
                },
                onCityClear: () => {
                    if (isHomeCity) {
                        this.homeCityId = '';
                        this.homeCityName = '';
                        this.homeCityCountryIso = '';
                    } else {
                        this.cityId = '';
                        this.cityName = '';
                        this.cityCountryIso = '';
                    }
                },
                isHomeCity,
                initialCountryIso
            });

            if (instance) {
                this._autocompleteInstances[this.currentStep] = instance;
            }
        },

        cleanupLocationAutocomplete() {
            $('input[name="_userLocation"], input[name="_userOrigin"]').each(function() {
                cleanupCityAutocomplete($(this));
            });
            $(document).off('.nearby');
            this._autocompleteInstances = {};
        },

        // Form submission
        async submitSignup() {
            this.isSubmitting = true;
            this.errors = {};

            // Only validate current step - let API validate everything
            if (!this.isStepValid(this.currentStep)) {
                this.markTouched();
                this.isSubmitting = false;
                return;
            }

            // Submit to API
            const result = await submitSignup(this);

            if (result.success) {
                // Clear saved state
                clearState();

                // Redirect to email verification
                window.location.href = '/app/getting-started/email';
            } else {
                this.isSubmitting = false;

                if (result.validationErrors) {
                    // Find which steps have errors
                    const fieldStepMap = getFieldStepMap();
                    const errorFields = Object.keys(result.validationErrors);
                    let targetStep = null;

                    // First check if current step has an error - if so, stay here
                    for (const field of errorFields) {
                        const step = fieldStepMap[field];
                        if (step === this.currentStep) {
                            targetStep = this.currentStep;
                            break;
                        }
                    }

                    // Otherwise go to first step with error
                    if (targetStep === null) {
                        let firstErrorStep = 8;
                        for (const field of errorFields) {
                            const step = fieldStepMap[field];
                            if (step && step < firstErrorStep) {
                                firstErrorStep = step;
                            }
                        }
                        targetStep = firstErrorStep;
                    }

                    // Set errors and mark step as touched
                    this.errors = result.validationErrors;
                    markStepTouched(targetStep, this);

                    if (targetStep !== this.currentStep) {
                        this.goToStep(targetStep);
                    }
                } else {
                    this.errors = {
                        general: { msg: result.error }
                    };
                }
            }
        },

        // Cleanup
        destroy() {
            this.cleanupLocationAutocomplete();
            this.userLat = null;
            this.userLon = null;
        }
    };
}
